import React, { Fragment, useMemo, useState } from 'react';
import AppTable from '../../../../constants/AppTable';
import { LoadingSpinner } from '../../../../constants/AppLoading';
import DialogBox from '../../../../constants/DialogBox';
import { Grid } from '@mui/material';
import InputTextBox from '../../../../constants/InputTextBox';
import { useFormik } from 'formik';
import NepaliInputBox from '../../../../constants/NepaliInputBox';
import { handleNepaliInputChange } from '../../../../helpers/handleChange';
import { EndBaseLine } from '../../../../constants/AppSpace';
import { Button } from '../../../../constants/Button';
import { putDataToTheServer } from '../../../../helpers/api';
import AppURL from '../../../../constants/AppURL';
import { ToastSuccess, ToastWarning } from '../../../../constants/Toast';
import { ProductUnit } from '../../../../helpers/models/Models';
import DefaultSelectInputBox from '../../../../constants/DefaultSelectBox';
import { YesNoOptions } from '../../../../constants/Options';

interface ProductUnitListViewProps {
    results: {
        results: ProductUnit[];
        previous: string | null;
        next: string | null;
    } | null;
    loading: boolean;
    error: string | null;
    setOnURL: React.Dispatch<React.SetStateAction<string>>;
    handleFormSuccess: () => void;
}

const ProductUnitListView: React.FC<ProductUnitListViewProps> = ({ results, loading, error, handleFormSuccess, setOnURL }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState<ProductUnit | null>(null);

    const columns = [
        { header: 'SN', accessor: 'sn', sortable: false },
        { header: 'Unit Name', accessor: 'unit_name', sortable: true },
        { header: 'Short', accessor: 'short_name', sortable: false },
        { header: 'Remarks', accessor: 'remarks', sortable: false },
        { header: 'Actions', accessor: 'actions', action: true },
    ];
    const modifiedData = useMemo(() => {
        if (!results) return [];
        return results.results.map((item: ProductUnit) => ({
            sn: item.sn,
            unit_name: item.unit_name,
            short_name: item.short_name,
            active: item.status,
            status: item.status,
            remarks: item.remarks
        }));
    }, [results]);

    const handleEdit = (item: ProductUnit) => {
        setCurrentItem(item);
        setDialogOpen(true);
    };

    const formik = useFormik({
        initialValues: {
            unit_name: currentItem?.unit_name || "",
            short_name: currentItem?.short_name || "",
            remarks: currentItem?.remarks || "",
            status: currentItem?.status === 1 ? "1" : "2" || "",
        },
        // validationSchema: readerEditValidationSchema,
        enableReinitialize: true,
        onSubmit: async (values, action) => {
            const formData = new FormData();
            formData.append("unit_name", values.unit_name);
            formData.append("short_name", values.short_name);
            formData.append("remarks", values.remarks);
            formData.append("status", values.status === "2" ? "0" : "1");
            try {
                await putDataToTheServer(AppURL.InventoryURL + 'inventory/product/unit/retrieve/update/view/' + currentItem?.sn, formData).then((res) => {
                    if (res?.status === 200) {
                        handleFormSuccess();
                        setDialogOpen(false);
                        ToastSuccess({ message: res?.message });
                    } else if (res?.status === 400) {
                        ToastWarning({ message: res?.message });
                    }
                    else {
                        ToastWarning({ message: "Some Issue" });
                    }
                });
            } catch (error) {
                console.log(error);
            }
        },
    });

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return <p>Error loading data: {error}</p>;
    }

    return (
        <Fragment>
            <AppTable
                columns={columns}
                data={modifiedData}
                onEdit={handleEdit}
                onNext={() => results?.next && setOnURL(results.next)}
                onPrev={() => results?.previous && setOnURL(results.previous)}
            />
            {dialogOpen && currentItem && (
                <DialogBox open={dialogOpen} headerName='Product Unit Edit Update' close={() => setDialogOpen(false)}>
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container columnSpacing={3} rowSpacing={0.8}>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='unit_name'
                                    label='Unit Name'
                                    handleChange={formik.handleChange}
                                    value={formik.values.unit_name}
                                    error={typeof formik.errors.unit_name === 'string' ? formik.errors.unit_name : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.unit_name}
                                    required={true}
                                    placeholder='Enter Unit Name'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='short_name'
                                    label='Short Name'
                                    handleChange={formik.handleChange}
                                    value={formik.values.short_name}
                                    error={typeof formik.errors.unit_name === 'string' ? formik.errors.short_name : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.short_name}
                                    required={true}
                                    placeholder='Enter Unit Name'
                                />
                            </Grid>
                
                       
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='remarks'
                                    label='Remarks'
                                    handleChange={formik.handleChange}
                                    value={formik.values.remarks}
                                    error={typeof formik.errors.remarks === 'string' ? formik.errors.remarks : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.remarks}
                                    required={false}
                                    placeholder='Enter Remarks'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <DefaultSelectInputBox
                                    name="status"
                                    label='Active'
                                    disabled={false} // Adjust based on your needs
                                    options={YesNoOptions}
                                    value={formik.values.status}
                                    optionSelect={(name, selectedOption) => {
                                        formik.setFieldValue(name, selectedOption.value);
                                    }}
                                    clear={true}
                                    placeholder="Select Options"
                                />
                            </Grid>

                            <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                                <EndBaseLine top={18}>
                                    <Button btn_type='update' type='submit' el='button' />
                                </EndBaseLine>
                            </Grid>
                        </Grid>
                    </form>
                </DialogBox>
            )}
        </Fragment>
    );
};

export default ProductUnitListView;
